import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import {
  AnalyzeUploadedTemplateMetadata,
  AnalyzeUploadedTemplateResponse,
  RegisterMasterTemplateMetadata,
  RegisterMasterTemplateResponse,
  RenderDocumentRequest,
  UpdateMasterTemplateMetadata,
} from '../models/document-asset-analysis.model';
import {
  DocumentAssetApiResponse,
  DocumentAssetListFilter,
  DocumentAssetSummary,
} from '../models/document-asset.model';
import { MasterTemplateDetail } from '../models/document-template-binding.model';

/**
 * Client HTTP tipizzato per gli endpoint V2 dei master template DOCX
 * (`/api/v2/document-templates`).
 */
@Injectable({ providedIn: 'root' })
export class DocumentTemplateV2Service {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backofficeApiUrl}/api/v2/document-templates`;

  list(
    filter?: DocumentAssetListFilter,
  ): Observable<DocumentAssetApiResponse<DocumentAssetSummary[]>> {
    return this.http.get<DocumentAssetApiResponse<DocumentAssetSummary[]>>(
      this.baseUrl,
      { params: this.buildListParams(filter) },
    );
  }

  /**
   * Lista i master template ACTIVE filtrando client-side per `subDomainType`.
   * Convenzione: `subDomainType` è una chiave simbolica maiuscola come
   * `CONTRACTS` (template contratto) o `ATTO_DI_NOMINA` (template atto di
   * nomina). Usato dai dropdown FE dei form contratto / azione.
   *
   * @param domainType   Filtro back-end (`SUPPLIERS` / `PROFESSIONALS`).
   * @param subDomainType Filtro client-side (`CONTRACTS` / `ATTO_DI_NOMINA`).
   * @param includeInactive Se true include i DRAFT/RETIRED; default false.
   */
  listMasterTemplates(
    domainType: string,
    subDomainType: string,
    includeInactive = false,
  ): Observable<DocumentAssetSummary[]> {
    return this.list({ domainType, includeInactive }).pipe(
      map((response) => {
        const items = response?.data ?? [];
        return items.filter(
          (asset) =>
            asset.subDomainType === subDomainType
            && (includeInactive || asset.status === 'ACTIVE'),
        );
      }),
    );
  }

  /**
   * Ritorna la versione più recente di un master template dato il suo
   * businessKey, oppure `null` se il businessKey non ha versioni.
   * Usato quando il FE ha persistito solo il businessKey (es. campo
   * `contract_template_id` / `atto_di_nomina_template_id` post-migrazione 007)
   * e serve risalire al nome del template per la UI.
   */
  getByBusinessKey(
    businessKey: string,
  ): Observable<DocumentAssetSummary | null> {
    return this.listVersions(businessKey).pipe(
      map((response) => response?.data?.[0] ?? null),
    );
  }

  /**
   * Returns the next numeric progressive (1-based) to prefix a fresh master
   * template businessKey (e.g. `003_MY_TEMPLATE`). Prevents businessKey
   * collisions when different templates share the same human name.
   */
  getNextProgressive(): Observable<DocumentAssetApiResponse<number>> {
    return this.http.get<DocumentAssetApiResponse<number>>(
      `${this.baseUrl}/next-progressive`,
    );
  }

  get(
    assetId: number,
  ): Observable<DocumentAssetApiResponse<MasterTemplateDetail>> {
    return this.http.get<DocumentAssetApiResponse<MasterTemplateDetail>>(
      `${this.baseUrl}/${assetId}`,
    );
  }

  listVersions(
    businessKey: string,
  ): Observable<DocumentAssetApiResponse<DocumentAssetSummary[]>> {
    return this.http.get<DocumentAssetApiResponse<DocumentAssetSummary[]>>(
      `${this.baseUrl}/versions/${encodeURIComponent(businessKey)}`,
    );
  }

  analyzeUpload(
    file: File,
    metadata: AnalyzeUploadedTemplateMetadata,
  ): Observable<DocumentAssetApiResponse<AnalyzeUploadedTemplateResponse>> {
    const body = this.buildMultipart(file, metadata);
    return this.http.post<
      DocumentAssetApiResponse<AnalyzeUploadedTemplateResponse>
    >(`${this.baseUrl}/analyze-upload`, body);
  }

  analyzeRegistered(
    templateId: number,
  ): Observable<DocumentAssetApiResponse<AnalyzeUploadedTemplateResponse>> {
    return this.http.post<
      DocumentAssetApiResponse<AnalyzeUploadedTemplateResponse>
    >(`${this.baseUrl}/${templateId}/analyze`, null);
  }

  registerMaster(
    file: File,
    metadata: RegisterMasterTemplateMetadata,
  ): Observable<DocumentAssetApiResponse<RegisterMasterTemplateResponse>> {
    const body = this.buildMultipart(file, metadata);
    return this.http.post<
      DocumentAssetApiResponse<RegisterMasterTemplateResponse>
    >(`${this.baseUrl}/register-master`, body);
  }

  update(
    templateId: number,
    metadata: UpdateMasterTemplateMetadata,
  ): Observable<DocumentAssetApiResponse<RegisterMasterTemplateResponse>> {
    return this.http.put<
      DocumentAssetApiResponse<RegisterMasterTemplateResponse>
    >(`${this.baseUrl}/${templateId}`, metadata);
  }

  softDelete(
    templateId: number,
  ): Observable<DocumentAssetApiResponse<RegisterMasterTemplateResponse>> {
    return this.http.delete<
      DocumentAssetApiResponse<RegisterMasterTemplateResponse>
    >(`${this.baseUrl}/${templateId}`);
  }

  download(assetId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${assetId}/download`, {
      responseType: 'blob',
    });
  }

  renderDocx(
    businessKey: string,
    request: RenderDocumentRequest,
  ): Observable<Blob> {
    return this.http.post(
      `${this.baseUrl}/${encodeURIComponent(businessKey)}/render-docx`,
      request,
      { responseType: 'blob' },
    );
  }

  renderPdf(
    businessKey: string,
    request: RenderDocumentRequest,
  ): Observable<Blob> {
    return this.http.post(
      `${this.baseUrl}/${encodeURIComponent(businessKey)}/render-pdf`,
      request,
      { responseType: 'blob' },
    );
  }

  private buildListParams(filter?: DocumentAssetListFilter): HttpParams {
    let params = new HttpParams();
    if (!filter) return params;
    if (filter.domainType) params = params.set('domainType', filter.domainType);
    if (filter.status) params = params.set('status', filter.status);
    if (filter.includeInactive !== undefined) {
      params = params.set('includeInactive', String(filter.includeInactive));
    }
    return params;
  }

  private buildMultipart(file: File, metadata: object): FormData {
    const body = new FormData();
    body.append('file', file, file.name);
    body.append(
      'metadata',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
    );
    return body;
  }
}
